import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ActivityIcon } from '../../../assets/icons/activety-icon.svg';
import { ReactComponent as DotCircle } from '../../../assets/icons/dot-circle.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/refresh-circle.svg';
import { ReactComponent as ResetPasswordIcon } from '../../../assets/icons/reset-password-v1-icon.svg';
import { appCurrentBranding, useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { branding } from '../../../config/enum';
import { ApiRequest } from '../../../interface/common.interface';
import {
  EndConsumerInterFace,
  RequestEndConsumerInterFace,
} from '../../../interface/end-consumer.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableEndConsumeFiltersApplied,
  fetchAllEndConsumers,
  fetchListEndConsumerFilters,
  updateEndConsumerFilters,
  setSortData,
} from '../../../redux/slices/EndConsumerManagementSlice';
import { setSelectedEndConsumer } from '../../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ConfirmBox from '../../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import SidePaneModel from '../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import userApi from '../../../utils/api/end-consumer';
import endConsumerApi from '../../../utils/api/end-consumer-v2';

import ActivityTimeline from './EndConsumerProfile/ActivityTimeline';

/**
 * End consumers list component
 * @returns {JSX.Element} - Returns the end consumers list component.
 */
function EndConsumersList() {
  const appBranding = useAppBranding();
  const { t } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.endConsumer);
  const { endConsumerType } = useAppSelector((state) => state.endConsumerType);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSidePaneOpen, setIsSidePaneOpen] = useState(false);
  const [modalView, setModalView] = useState({
    status: false,
    title: t('addAccount'),
    header: true,
    content: <div />,
    rightCorner: '',
  });
  const [SidePaneView, setSidePaneView] = useState({
    title: '',
    content: <div />,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [confirmBox, setConfirmBox] = useState({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: t('ok'),
    /**
     * Demo function
     */
    conformSubmit: () => {},
  });

  const [active, setActive] = useState<any>({
    activeEndConsumers: 0,
    inactiveEndConsumers: 0,
    totalEndConsumers: 0,
  });

  /**
   * Sends a password reset link to the user's email for a specific consumer.
   * @param {string} currentData - The ID of the consumer whose password is to be reset.
   */
  const confirmResetPassword = (currentData: string) => {
    userApi
      .resetPassword(currentData, { submissionStatus: 'userCustomId' })
      .then(() => {
        setIsNotificationOpen(true);
        setIsSidePaneOpen(false);
        setConfirmBox((previous) => ({ ...previous, status: false }));
        setModalView((previous) => ({ ...previous, status: false }));
        dispatch(
          setNotificationMessage({
            message: t('passwordResetSuccess', { id: currentData }),
            status: true,
            type: 'success',
            code: 200,
          }),
        );
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message:
              error?.response?.data?.message || t('passwordResetFailed', { id: currentData }),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Fetch Users list function
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      dispatch(fetchAllEndConsumers(data));
    }
  };

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Displays a confirmation prompt to reset the password for a specific consumer.
   *
   * @param {string} currentData - The ID of the consumer whose password is to be reset.
   */
  const resetPasswordFunction = (currentData: string) => {
    setConfirmBox({
      status: true,
      title: t('resetPasswordTitle'),
      subTitle: t('resetPasswordMessage'),
      /**
       * Triggers the password reset process for the specified consumer.
       * @returns {void}
       */
      conformSubmit: () => confirmResetPassword(currentData), // ✅ Now correctly calls the function
      submitLabel: t('proceed'),
    });
  };

  /**
   * Handles the opening of the activity log for a specific consumer.
   * @param {EndConsumerInterFace} currentData - The consumer data for which the activity log is to be opened.
   */
  const handleOpenActivityLog = (currentData: EndConsumerInterFace, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`?userCustomId=${currentData.userCustomId}`);
    setIsSidePaneOpen(true);
    setSidePaneView({
      title: t('activityLog'),
      content: <ActivityTimeline />,
    });
  };

  /**
   * Handles the opening of the reset password form for a specific consumer.
   * @param {EndConsumerInterFace} currentData - The consumer data for which the reset password form is to be opened.
   */
  const handleResetPassword = (currentData: EndConsumerInterFace, event: React.MouseEvent) => {
    event.stopPropagation();
    setConfirmBox({
      status: true,
      title: t('areYouSureYouWantToContinue'),
      subTitle: t('resetPasswordDescription'),
      /**
       * Triggers the password reset process for the specified consumer.
       */
      conformSubmit: () => confirmResetPassword(currentData.userCustomId),
      submitLabel: t('proceed'),
    });
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof EndConsumerInterFace,
      sortAllow: true,
    },
    {
      label: t('consumerId'),
      accessorKey: 'userCustomId' as keyof EndConsumerInterFace,
      sortAllow: false,
      /**
       * No of stations
       */
      customData: (currentRow: EndConsumerInterFace) => (
        <p className="hyper-link-coloum">{currentRow.userCustomId || '-'}</p>
      ),
    },
    {
      label: t('name'),
      accessorKey: 'firstName' as keyof EndConsumerInterFace,
      sortAllow: true,
      /**
       * name
       * @param row
       * @returns
       */
      customData: (row: EndConsumerInterFace) => <span>{row?.name}</span>,
    },
    {
      label: t('phoneNumber'),
      accessorKey: 'phoneNumber' as keyof EndConsumerInterFace,
      sortAllow: false,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: EndConsumerInterFace) =>
        currentRow?.countryCode && currentRow?.phoneNumber ? (
          <div>
            +{currentRow.countryCode} {currentRow.phoneNumber}
          </div>
        ) : (
          <div>{t('na')}</div>
        ),
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof EndConsumerInterFace,
      sortAllow: false,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: EndConsumerInterFace) =>
        currentRow?.email ? <div>{currentRow.email}</div> : <div>{t('na')}</div>,
      hideWhenSidePanelOpen: true,
    },
    ...(appCurrentBranding !== branding.VOLTIE
      ? [
          {
            label: t('walletBalance'),
            accessorKey: 'wallet' as keyof EndConsumerInterFace,
            sortAllow: true,
            /**
             * Data custom function
             * @param currentRow
             */
            customData: (currentRow: EndConsumerInterFace) => (
              <div className="value-container">
                <p className="value">{ToFixedValue(currentRow.wallet)}</p>
                <span>{appBranding.text.currency}</span>
              </div>
            ),
          },
        ]
      : []),
    {
      label: t('status'),
      accessorKey: 'status' as keyof EndConsumerInterFace,
      position: 'center',
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: EndConsumerInterFace) =>
        currentRow.isActive ? (
          <div className="tag-success-label">
            <DotCircle /> <span>{t('active')}</span>
          </div>
        ) : (
          <div className="tag-danger-label">
            <DotCircle /> <span>{t('inactive')}</span>
          </div>
        ),
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('action'),
      accessorKey: '' as keyof EndConsumerInterFace,
      columnWidth: 100,
      /**
       * Function for action buttons
       * @param currentData
       * @returns
       */
      customData: (currentData: EndConsumerInterFace) => (
        <div className="action-button-group">
          <div className="device-listing-actions-wrap">
            <span
              role="presentation"
              onClick={(event) => handleOpenActivityLog(currentData, event)}>
              <ActivityIcon />
            </span>
            <span role="presentation" onClick={(event) => handleResetPassword(currentData, event)}>
              <ResetPasswordIcon />
            </span>
          </div>
          {appCurrentBranding === branding.VOLTIE ? (
            <div className="action-button-group">
              <div className="device-listing-actions-wrap">
                <span
                  role="presentation"
                  onClick={() => resetPasswordFunction(currentData.userCustomId)}>
                  <RefreshIcon />
                </span>
              </div>
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('consumerId'), accessorKey: 'userCustomId' },
    { label: t('name'), accessorKey: 'firstName' },
    { label: t('phoneNumber'), accessorKey: 'phoneNumber' },
    { label: t('email'), accessorKey: 'email' },
    { label: t('walletBalance'), accessorKey: 'wallet' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter((col) => {
    if (col.accessorKey === 'wallet' && appCurrentBranding === branding.VOLTIE) {
      return false;
    }

    return col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };
    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;
    userApi
      .exportConsumer(payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: EndConsumerInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return ConvertUTCtoDate(row.createdAt) || t('na');

              case 'firstName':
                return `${row.firstName || ''} ${row.lastName || ''}`.trim() || t('na');

              case 'phoneNumber':
                return row?.countryCode && row?.phoneNumber
                  ? `+${row.countryCode} ${row.phoneNumber}`
                  : t('na');

              case 'wallet':
                return row.wallet
                  ? `${ToFixedValue(row.wallet)} ${appBranding.text.currency}`
                  : `0.00 ${appBranding.text.currency}`;

              case 'status':
                return row.isActive ? t('active') : t('inactive');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'consumer-users-list.csv');
        } else {
          exportPDF(headers, rows, 'consumer-users-list.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: RequestEndConsumerInterFace) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Function to open end consumer profile
   * @param userCustomId - User custom id
   */
  const handleOpenEndConsumerProfile = (userCustomId: string) => {
    const currentRow = consumers.find((row) => row.userCustomId === userCustomId);
    if (currentRow) {
      navigate(`/ev-admin/end-consumer/${endConsumerType}/profile/${currentRow.userCustomId}`);

      dispatch(
        setSelectedEndConsumer({
          id: currentRow?._id,
          userCustomId: currentRow?.userCustomId,
        }),
      );
    }
  };

  const topLeftContent = (
    <div className="end-consumer-list-top-left-content">
      <div className="counts">
        <p className="count">{active?.totalEndConsumers || 0}</p>
      </div>
      <div className="status active">
        <div className="indicator" />
        <p className="label">{t('active')}</p>
        <p className="count">{active?.activeEndConsumers || 0}</p>
      </div>
      <div className="status inactive">
        <div className="indicator" />
        <p className="label">{t('inactive')}</p>
        <p className="count">{active?.inactiveEndConsumers || 0}</p>
      </div>
    </div>
  );

  useEffect(() => {
    if (isFilterUpdated || consumers.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableEndConsumeFiltersApplied());
    }
  }, [filters]);

  /**
   * Fetching card list api
   */
  const handleEndConsumerStatus = async () => {
    try {
      const response = await endConsumerApi.fetchEndConsumerStatus();

      const { activeEndConsumers, inactiveEndConsumers, totalEndConsumers } = response?.data || {};
      setActive({
        activeEndConsumers: activeEndConsumers || 0,
        inactiveEndConsumers: inactiveEndConsumers || 0,
        totalEndConsumers: totalEndConsumers || 0,
      });
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    handleEndConsumerStatus();
  }, []);

  return (
    <div className="end-consumer-list-wrapper">
      <PageTitle title={t('endConsumerList')} topLeftContent={topLeftContent} />
      <ModalBox
        open={isNotificationOpen}
        title=""
        header={false}
        rightCorner=""
        notification
        content={
          <SuccessNotification
            title={t('passwordResetSuccessNotification')}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setIsNotificationOpen(false); // Hide the notification after the timer or when closed
            }}
          />
        }
        closeModal={() => setIsNotificationOpen(false)}
      />
      <SidePaneModel
        open={isSidePaneOpen}
        title={SidePaneView.title}
        content={SidePaneView.content}
        closePanel={() => setIsSidePaneOpen(false)}
      />
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />

      <ConfirmBox
        open={confirmBox.status}
        title={confirmBox.title}
        subTitle={confirmBox.subTitle}
        conformSubmit={confirmBox.conformSubmit}
        closeModal={() => setConfirmBox((previous) => ({ ...previous, status: false }))}
        submitLabel={confirmBox.submitLabel}
      />

      <CustomTable
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={window.innerHeight >= 700 ? 320 : 329}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateEndConsumerFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListEndConsumerFilters}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        hoverLabel={t('refresh')}
        expandTableOpen
        leftTopContent={<> </>}
        expandTableOpenKey="EndConsumerManagementV2List"
        rowClickEvent={handleOpenEndConsumerProfile}
        rowClickParamsKey="userCustomId"
        ReferenceCurrentPages={handleRefresh}
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

EndConsumersList.defaultProps = {
  handleExpand: null,
};
export default EndConsumersList;
